import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail,
  Inbox,
  Send,
  FileText,
  Trash2,
  Star,
  Search,
  Plus,
  ArrowRight,
  LogOut,
  RefreshCw,
  Paperclip,
  MoreVertical,
  Reply,
  Forward,
  Archive,
  Clock,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  User,
  AlertCircle,
  Download,
  Globe,
  CloudDownload,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FOLDERS = [
  { id: 'inbox', name: 'صندوق الوارد', icon: Inbox },
  { id: 'sent', name: 'المرسل', icon: Send },
  { id: 'drafts', name: 'المسودات', icon: FileText },
  { id: 'starred', name: 'المميزة', icon: Star },
  { id: 'trash', name: 'المهملات', icon: Trash2 },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'منخفضة', color: 'text-gray-500' },
  { value: 'normal', label: 'عادية', color: 'text-blue-500' },
  { value: 'high', label: 'عالية', color: 'text-orange-500' },
  { value: 'urgent', label: 'عاجلة', color: 'text-red-500' },
];

export default function EmailInbox() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailThread, setEmailThread] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ unread: 0, drafts: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // حالات إنشاء بريد جديد
  const [composeData, setComposeData] = useState({
    recipients: [],
    subject: '',
    body: '',
    priority: 'normal',
    attachments: [],
    related_task_id: null,
    is_reply: false,
    is_forwarded: false,
    reply_to_id: null,
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [isExternalEmail, setIsExternalEmail] = useState(false);
  const [externalRecipient, setExternalRecipient] = useState('');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.role === 'client') {
        navigate('/client-portal');
        return;
      }
    }
    fetchEmails();
    fetchEmployees();
    fetchStats();
  }, [activeFolder, page]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = `/emails/${activeFolder}`;
      if (activeFolder === 'inbox') endpoint = '/emails/inbox';
      
      const res = await axios.get(`${API}${endpoint}?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmails(res.data.emails || res.data);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Error fetching emails:', err);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // جلب جميع الموظفين والمدراء للبريد
      const res = await axios.get(`${API}/employees?include_admins=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // استبعاد المستخدم الحالي من القائمة
      const userStr = localStorage.getItem('user');
      let filteredEmployees = res.data;
      if (userStr) {
        const currentUser = JSON.parse(userStr);
        filteredEmployees = res.data.filter(emp => emp.id !== currentUser.id);
      }
      
      setEmployees(filteredEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/emails/stats/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const syncExternalEmails = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/emails/external/sync`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.message);
      fetchEmails();
      fetchStats();
    } catch (err) {
      console.error('Error syncing emails:', err);
      alert('حدث خطأ أثناء المزامنة: ' + (err.response?.data?.detail || err.message));
    }
    setSyncing(false);
  };

  const fetchEmailDetails = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/emails/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmail(res.data.email);
      setEmailThread(res.data.thread || []);
      fetchStats(); // تحديث عدد غير المقروء
    } catch (err) {
      console.error('Error fetching email:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const openCompose = (replyTo = null, forward = false) => {
    // إعادة تعيين حالة البريد الخارجي
    setIsExternalEmail(false);
    setExternalRecipient('');
    
    if (replyTo) {
      // إذا كان رد على بريد خارجي
      if (replyTo.is_external && replyTo.external_email) {
        setIsExternalEmail(true);
        setExternalRecipient(replyTo.external_email);
      }
      
      setComposeData({
        recipients: forward ? [] : [{ id: replyTo.sender_id, name: replyTo.sender_name, email: replyTo.sender_email, type: 'to' }],
        subject: forward ? `إعادة توجيه: ${replyTo.subject}` : `رد: ${replyTo.subject}`,
        body: forward 
          ? `\n\n---------- الرسالة الأصلية ----------\nمن: ${replyTo.sender_name}\nالتاريخ: ${formatDate(replyTo.sent_at)}\nالموضوع: ${replyTo.subject}\n\n${replyTo.body}`
          : `\n\n---------- في ${formatDate(replyTo.sent_at)} كتب ${replyTo.sender_name}: ----------\n${replyTo.body}`,
        priority: 'normal',
        attachments: [],
        related_task_id: replyTo.related_task_id,
        is_reply: !forward,
        is_forwarded: forward,
        reply_to_id: replyTo.id,
      });
    } else {
      setComposeData({
        recipients: [],
        subject: '',
        body: '',
        priority: 'normal',
        attachments: [],
        related_task_id: null,
        is_reply: false,
        is_forwarded: false,
        reply_to_id: null,
      });
    }
    setShowCompose(true);
  };

  const sendEmail = async (saveAsDraft = false) => {
    // التحقق من صحة البيانات
    if (isExternalEmail) {
      // بريد خارجي
      if (!saveAsDraft && !externalRecipient.trim()) {
        alert('يرجى إدخال البريد الإلكتروني للمستلم');
        return;
      }
      if (!saveAsDraft && !composeData.subject.trim()) {
        alert('يرجى إدخال موضوع الرسالة');
        return;
      }
      
      setSendingEmail(true);
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/emails/external/send`, {
          to_email: externalRecipient,
          subject: composeData.subject,
          body: composeData.body,
          attachments: composeData.attachments
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        alert('تم إرسال البريد الخارجي بنجاح');
        setShowCompose(false);
        setIsExternalEmail(false);
        setExternalRecipient('');
        fetchEmails();
        fetchStats();
      } catch (err) {
        console.error('Error sending external email:', err);
        alert('حدث خطأ أثناء الإرسال: ' + (err.response?.data?.detail || err.message));
      }
      setSendingEmail(false);
      return;
    }
    
    // بريد داخلي
    if (!saveAsDraft && composeData.recipients.length === 0) {
      alert('يرجى إضافة مستلم واحد على الأقل');
      return;
    }
    if (!saveAsDraft && !composeData.subject.trim()) {
      alert('يرجى إدخال موضوع الرسالة');
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/emails/compose`, {
        ...composeData,
        save_as_draft: saveAsDraft
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(saveAsDraft ? 'تم حفظ المسودة' : 'تم إرسال البريد بنجاح');
      setShowCompose(false);
      fetchEmails();
      fetchStats();
    } catch (err) {
      console.error('Error sending email:', err);
      alert('حدث خطأ أثناء الإرسال');
    }
    setSendingEmail(false);
  };

  const toggleStar = async (emailId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const email = emails.find(e => e.id === emailId);
      await axios.put(`${API}/emails/${emailId}`, {
        is_starred: !email?.is_starred
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
    } catch (err) {
      console.error('Error toggling star:', err);
    }
  };

  const deleteEmail = async (emailId, permanent = false) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/emails/${emailId}?permanent=${permanent}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmail(null);
      fetchEmails();
      fetchStats();
    } catch (err) {
      console.error('Error deleting email:', err);
    }
  };

  const restoreEmail = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/emails/${emailId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
    } catch (err) {
      console.error('Error restoring email:', err);
    }
  };

  const addRecipient = (employee) => {
    if (!composeData.recipients.find(r => r.id === employee.id)) {
      setComposeData(prev => ({
        ...prev,
        recipients: [...prev.recipients, {
          id: employee.id,
          name: employee.full_name,
          email: employee.email,
          type: 'to'
        }]
      }));
    }
    setShowRecipientDropdown(false);
    setRecipientSearchQuery('');
  };

  const removeRecipient = (recipientId) => {
    setComposeData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.id !== recipientId)
    }));
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const newAttachments = [];
    for (const file of files) {
      // تحويل الملف إلى base64
      const reader = new FileReader();
      const fileData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // استخراج البيانات فقط (بدون prefix)
      const base64Data = fileData.split(',')[1];
      
      newAttachments.push({
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64Data
      });
    }
    
    setComposeData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments]
    }));
  };

  const removeAttachment = (index) => {
    setComposeData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const downloadAttachment = (attachment) => {
    if (attachment.data) {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && emails.length === 0) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl">
        <div className="animate-spin w-12 h-12 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <h1 className="text-xl font-heading font-bold text-white">البريد الإلكتروني</h1>
              <p className="text-sm text-gray-300 font-body">{user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={syncExternalEmails}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all disabled:opacity-50"
              data-testid="sync-btn"
              title="مزامنة البريد الخارجي"
            >
              <CloudDownload className={`w-4 h-4 ${syncing ? 'animate-bounce' : ''}`} />
              {syncing ? 'جاري المزامنة...' : 'مزامنة'}
            </button>
            <button
              onClick={() => openCompose()}
              className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-md hover:bg-gold/90 transition-all"
              data-testid="compose-btn"
            >
              <Plus className="w-4 h-4" />
              رسالة جديدة
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-md hover:bg-white/20 transition-all"
              data-testid="back-to-dashboard"
            >
              <ArrowRight className="w-4 h-4" />
              لوحة التحكم
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Compose Buttons */}
              <div className="p-4 space-y-2">
                <button
                  onClick={() => openCompose()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-white rounded-lg hover:bg-gold/90 transition-all font-body"
                  data-testid="compose-sidebar-btn"
                >
                  <Plus className="w-5 h-5" />
                  رسالة داخلية
                </button>
                <button
                  onClick={() => {
                    setIsExternalEmail(true);
                    setExternalRecipient('');
                    setComposeData({
                      recipients: [],
                      subject: '',
                      body: '',
                      priority: 'normal',
                      attachments: [],
                      related_task_id: null,
                      is_reply: false,
                      is_forwarded: false,
                      reply_to_id: null,
                    });
                    setShowCompose(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-body"
                  data-testid="compose-external-btn"
                >
                  <Globe className="w-5 h-5" />
                  رسالة خارجية
                </button>
              </div>

              {/* Folders */}
              <nav className="p-2">
                {FOLDERS.map((folder) => {
                  const Icon = folder.icon;
                  const count = folder.id === 'inbox' ? stats.unread : folder.id === 'drafts' ? stats.drafts : 0;
                  
                  return (
                    <button
                      key={folder.id}
                      onClick={() => { setActiveFolder(folder.id); setPage(1); setSelectedEmail(null); }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                        activeFolder === folder.id
                          ? 'bg-primary text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <span className="font-body">{folder.name}</span>
                      </div>
                      {count > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          activeFolder === folder.id ? 'bg-white/20' : 'bg-primary/10 text-primary'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex gap-4">
            {/* Email List */}
            <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${selectedEmail ? 'w-1/3' : 'flex-1'}`}>
              {/* Search & Refresh */}
              <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="بحث في البريد..."
                    className="w-full pr-10 pl-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary font-body"
                  />
                </div>
                <button
                  onClick={fetchEmails}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="تحديث"
                >
                  <RefreshCw className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Email List */}
              <div className="divide-y divide-gray-50 max-h-[calc(100vh-280px)] overflow-y-auto">
                {emails.length === 0 ? (
                  <div className="p-12 text-center">
                    <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-400 font-body">لا توجد رسائل</p>
                  </div>
                ) : (
                  emails.map((email) => (
                    <div
                      key={email.id}
                      onClick={() => fetchEmailDetails(email.id)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !email.is_read ? 'bg-blue-50/50' : ''
                      } ${selectedEmail?.id === email.id ? 'bg-primary/5 border-r-4 border-primary' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => toggleStar(email.id, e)}
                          className="mt-1"
                        >
                          <Star className={`w-4 h-4 ${email.is_starred ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {email.is_external && (
                                <Globe className="w-3 h-3 text-green-500" title="بريد خارجي" />
                              )}
                              <span className={`text-sm font-medium truncate ${!email.is_read ? 'font-bold text-primary' : 'text-gray-700'}`}>
                                {activeFolder === 'sent' ? email.recipients?.[0]?.name : email.sender_name}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(email.sent_at || email.created_at)}</span>
                          </div>
                          <p className={`text-sm truncate ${!email.is_read ? 'font-semibold' : 'text-gray-600'}`}>
                            {email.subject || '(بدون موضوع)'}
                          </p>
                          <p className="text-xs text-gray-400 truncate mt-1">{email.body?.substring(0, 60)}...</p>
                        </div>
                        {email.attachments?.length > 0 && (
                          <Paperclip className="w-4 h-4 text-gray-400 mt-1" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-100 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                  >
                    السابق
                  </button>
                  <span className="text-sm text-gray-500">
                    {page} من {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                  >
                    التالي
                  </button>
                </div>
              )}
            </div>

            {/* Email Detail */}
            {selectedEmail && (
              <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Email Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-lg font-bold text-primary">{selectedEmail.subject || '(بدون موضوع)'}</h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openCompose(selectedEmail, false)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="رد"
                      >
                        <Reply className="w-5 h-5 text-gray-500" />
                      </button>
                      <button
                        onClick={() => openCompose(selectedEmail, true)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="إعادة توجيه"
                      >
                        <Forward className="w-5 h-5 text-gray-500" />
                      </button>
                      <button
                        onClick={() => deleteEmail(selectedEmail.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="حذف"
                      >
                        <Trash2 className="w-5 h-5 text-gray-500" />
                      </button>
                      <button
                        onClick={() => setSelectedEmail(null)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{selectedEmail.sender_name}</p>
                      <p className="text-xs text-gray-500">{selectedEmail.sender_email}</p>
                    </div>
                    <div className="mr-auto text-left">
                      <p className="text-xs text-gray-400">{formatFullDate(selectedEmail.sent_at)}</p>
                    </div>
                  </div>
                  
                  {selectedEmail.recipients?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      إلى: {selectedEmail.recipients.map(r => r.name).join('، ')}
                    </p>
                  )}
                </div>

                {/* Email Body */}
                <div className="p-6 max-h-[calc(100vh-450px)] overflow-y-auto">
                  <div className="prose prose-sm max-w-none font-body whitespace-pre-wrap">
                    {selectedEmail.body}
                  </div>
                  
                  {/* Attachments */}
                  {selectedEmail.attachments?.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="text-sm font-bold text-gray-600 mb-3 flex items-center gap-2">
                        <Paperclip className="w-4 h-4" />
                        المرفقات ({selectedEmail.attachments.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedEmail.attachments.map((att, idx) => (
                          <button
                            key={idx}
                            onClick={() => downloadAttachment(att)}
                            className="px-3 py-2 bg-gray-100 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-200 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span>{att.name}</span>
                            <Download className="w-4 h-4 text-primary" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Related Task */}
                  {selectedEmail.related_task_number && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        مرتبط بالمهمة: <span className="font-bold">{selectedEmail.related_task_number}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Quick Reply */}
                <div className="p-4 border-t border-gray-100">
                  <button
                    onClick={() => openCompose(selectedEmail, false)}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors font-body"
                  >
                    اضغط للرد على هذه الرسالة...
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className={`p-4 ${isExternalEmail ? 'bg-green-600' : 'bg-primary'} text-white flex items-center justify-between`}>
              <h3 className="font-bold flex items-center gap-2">
                {isExternalEmail && <Globe className="w-5 h-5" />}
                {isExternalEmail ? 'رسالة خارجية' : composeData.is_reply ? 'رد على الرسالة' : composeData.is_forwarded ? 'إعادة توجيه' : 'رسالة داخلية'}
              </h3>
              <button onClick={() => { setShowCompose(false); setIsExternalEmail(false); }} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto" onClick={() => setShowRecipientDropdown(false)}>
              {/* External Email Recipient */}
              {isExternalEmail ? (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">البريد الإلكتروني للمستلم:</label>
                  <input
                    type="email"
                    value={externalRecipient}
                    onChange={(e) => setExternalRecipient(e.target.value)}
                    placeholder="example@domain.com"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500 font-body"
                    data-testid="external-recipient-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">سيتم إرسال البريد من: info@hklaw.sa</p>
                </div>
              ) : (
                /* Internal Recipients */
                <div onClick={(e) => e.stopPropagation()}>
                  <label className="block text-sm font-bold text-gray-700 mb-2">إلى:</label>
                  <div className="relative">
                    <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-lg min-h-[44px] items-center">
                      {composeData.recipients.map((r) => (
                        <span key={r.id} className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                          {r.name}
                          <button onClick={() => removeRecipient(r.id)}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={recipientSearchQuery}
                        onChange={(e) => {
                          setRecipientSearchQuery(e.target.value);
                          setShowRecipientDropdown(true);
                        }}
                        onFocus={() => setShowRecipientDropdown(true)}
                        placeholder="اكتب اسم المستلم للبحث..."
                        className="flex-1 min-w-[150px] outline-none border-none bg-transparent text-sm font-body"
                        data-testid="recipient-search-input"
                      />
                    </div>
                    
                    {showRecipientDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {employees
                          .filter(e => !composeData.recipients.find(r => r.id === e.id))
                          .filter(e => 
                            recipientSearchQuery === '' ||
                            e.full_name?.toLowerCase().includes(recipientSearchQuery.toLowerCase()) ||
                            e.email?.toLowerCase().includes(recipientSearchQuery.toLowerCase())
                          )
                          .map((emp) => (
                            <button
                              key={emp.id}
                              onClick={() => {
                                addRecipient(emp);
                                setRecipientSearchQuery('');
                              }}
                              className="w-full px-4 py-2 text-right hover:bg-gray-50 flex items-center gap-2"
                            >
                              <User className="w-4 h-4 text-gray-400" />
                              <span>{emp.full_name}</span>
                              <span className="text-xs text-gray-400">{emp.email}</span>
                            </button>
                          ))}
                        {employees
                          .filter(e => !composeData.recipients.find(r => r.id === e.id))
                          .filter(e => 
                            recipientSearchQuery === '' ||
                            e.full_name?.toLowerCase().includes(recipientSearchQuery.toLowerCase()) ||
                            e.email?.toLowerCase().includes(recipientSearchQuery.toLowerCase())
                          ).length === 0 && (
                          <div className="px-4 py-3 text-center text-gray-400 text-sm">
                            لا توجد نتائج
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الموضوع:</label>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary font-body"
                  placeholder="موضوع الرسالة"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الأولوية:</label>
                <div className="flex gap-2">
                  {PRIORITY_OPTIONS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setComposeData(prev => ({ ...prev, priority: p.value }))}
                      className={`px-3 py-1 rounded-lg text-sm transition-all ${
                        composeData.priority === p.value
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نص الرسالة:</label>
                <textarea
                  value={composeData.body}
                  onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary font-body resize-none"
                  rows={8}
                  placeholder="اكتب رسالتك هنا..."
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">المرفقات:</label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="email-attachments"
                  />
                  <label
                    htmlFor="email-attachments"
                    className="flex items-center justify-center gap-2 cursor-pointer text-gray-500 hover:text-primary transition-colors"
                  >
                    <Paperclip className="w-5 h-5" />
                    <span>اضغط لإضافة مرفقات</span>
                  </label>
                  
                  {composeData.attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {composeData.attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{att.name}</span>
                            <span className="text-xs text-gray-400">({Math.round(att.size / 1024)} KB)</span>
                          </div>
                          <button
                            onClick={() => removeAttachment(idx)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={() => sendEmail(true)}
                disabled={sendingEmail}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                حفظ كمسودة
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCompose(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => sendEmail(false)}
                  disabled={sendingEmail}
                  className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-lg hover:bg-gold/90 disabled:opacity-50 transition-colors"
                  data-testid="send-email-btn"
                >
                  {sendingEmail ? (
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      إرسال
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
